import React, { useState, useMemo } from 'react';
import { CustomerWithLoginStatus, ActivityStatus } from '../../../api/customer';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

interface CustomerActivityTableProps {
  customers: CustomerWithLoginStatus[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  activityFilter: ActivityStatus;
  onPageChange: (page: number) => void;
  onSearchChange?: (searchQuery: string) => void;
}

const CustomerActivityTable: React.FC<CustomerActivityTableProps> = ({
  customers,
  totalCount,
  currentPage,
  totalPages,
  loading,
  activityFilter,
  onPageChange,
  onSearchChange,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<'name' | 'email' | 'lastLogin' | 'daysSinceLogin'>('daysSinceLogin');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedCustomers = useMemo(() => {
    // Only apply client-side sorting (filtering done on server)
    const sorted = [...customers].sort((a, b) => {
      let aVal: any, bVal: any;

      switch (sortField) {
        case 'name':
          aVal = `${a.firstName} ${a.lastName}`.toLowerCase();
          bVal = `${b.firstName} ${b.lastName}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'lastLogin':
          aVal = a.lastLogin ? new Date(a.lastLogin).getTime() : 0;
          bVal = b.lastLogin ? new Date(b.lastLogin).getTime() : 0;
          break;
        case 'daysSinceLogin':
          aVal = a.daysSinceLogin ?? 999999;
          bVal = b.daysSinceLogin ?? 999999;
          break;
        default:
          return 0;
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return sorted;
  }, [customers, sortField, sortDirection]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status: ActivityStatus) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 border border-green-200">
            <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
            Active
          </span>
        );
      case 'warning':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 border border-yellow-200">
            <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
            At Risk
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 border border-red-200">
            <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
            Inactive
          </span>
        );
    }
  };

  const getRowClassName = (status: ActivityStatus) => {
    const base = 'border-b hover:bg-gray-50 cursor-pointer transition-colors';
    switch (status) {
      case 'active':
        return `${base} bg-white`;
      case 'warning':
        return `${base} bg-yellow-50 bg-opacity-30`;
      case 'inactive':
        return `${base} bg-red-50 bg-opacity-40`;
      default:
        return base;
    }
  };

  const formatLastLogin = (lastLogin: string | null | undefined, daysSince: number | null) => {
    if (!lastLogin) {
      return <span className="text-red-600 font-medium">Never logged in</span>;
    }

    return (
      <div>
        <p className="text-gray-900">{dayjs(lastLogin).format('MMM D, YYYY')}</p>
        <p className="text-xs text-gray-500">{dayjs(lastLogin).fromNow()}</p>
      </div>
    );
  };

  const formatDaysSince = (days: number | null) => {
    if (days === null) {
      return <span className="text-red-600 font-bold">Never</span>;
    }

    if (days === 0) {
      return <span className="text-green-600 font-medium">Today</span>;
    }

    const className = days <= 7 ? 'text-green-600' : days <= 30 ? 'text-yellow-600' : 'text-red-600';
    return <span className={`font-medium ${className}`}>{days} days</span>;
  };

  const handleRowClick = (customerId: string) => {
    navigate(`/customer-information/${customerId}`);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Customer Login Activity Details</h2>
        <p className="text-gray-600">Monitor which ACTIVE customers are actually using the platform</p>
      </div>

      {/* Status Badge */}
      <div className="mb-6">
        <div className="inline-flex items-center px-4 py-2 rounded-lg bg-blue-50 border border-blue-200">
          <span className="text-sm text-blue-700">
            Showing <span className="font-semibold">{activityFilter}</span> customers
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 border-b-2 border-gray-200">
            <tr>
              <th
                className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('name')}
              >
                <div className="flex items-center gap-1">
                  Name
                  {sortField === 'name' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th
                className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('email')}
              >
                <div className="flex items-center gap-1">
                  Email
                  {sortField === 'email' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th
                className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('lastLogin')}
              >
                <div className="flex items-center gap-1">
                  Last Login
                  {sortField === 'lastLogin' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th
                className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                onClick={() => handleSort('daysSinceLogin')}
              >
                <div className="flex items-center gap-1">
                  Days Since Login
                  {sortField === 'daysSinceLogin' && <span>{sortDirection === 'asc' ? '↑' : '↓'}</span>}
                </div>
              </th>
              <th className="text-left py-3 px-4 font-semibold text-gray-700">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="text-center py-12">
                  <div className="flex flex-col items-center gap-3">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <p className="text-gray-500">Loading customers...</p>
                  </div>
                </td>
              </tr>
            ) : sortedCustomers.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-8 text-gray-500">
                  No customers found
                </td>
              </tr>
            ) : (
              sortedCustomers.map((customer) => (
                <tr
                  key={customer._id}
                  className={getRowClassName(customer.activityStatus)}
                  onClick={() => handleRowClick(customer._id)}
                >
                  <td className="py-3 px-4">
                    <div className="font-medium text-gray-900">
                      {customer.firstName} {customer.lastName}
                    </div>
                  </td>
                  <td className="py-3 px-4 text-gray-700">{customer.email}</td>
                  <td className="py-3 px-4">
                    {formatLastLogin(customer.lastLogin, customer.daysSinceLogin)}
                  </td>
                  <td className="py-3 px-4">
                    {formatDaysSince(customer.daysSinceLogin)}
                  </td>
                  <td className="py-3 px-4">
                    {getStatusBadge(customer.activityStatus)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {!loading && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between border-t pt-4">
          <div className="text-sm text-gray-600">
            Showing page {currentPage + 1} of {totalPages} ({totalCount} total customers)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onPageChange(currentPage - 1)}
              disabled={currentPage === 0}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>

            {/* Page numbers */}
            <div className="flex gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i;
                } else if (currentPage < 3) {
                  pageNum = i;
                } else if (currentPage >= totalPages - 3) {
                  pageNum = totalPages - 5 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }

                return (
                  <button
                    key={pageNum}
                    onClick={() => onPageChange(pageNum)}
                    className={`px-3 py-2 rounded-lg text-sm font-medium ${
                      currentPage === pageNum
                        ? 'bg-blue-600 text-white'
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {pageNum + 1}
                  </button>
                );
              })}
            </div>

            <button
              onClick={() => onPageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerActivityTable;
